<template>
  <div class="detail-page">
    <!-- 顶部导航栏 -->
    <div class="header-bar">
      <div class="header-left" @click="onBack">
        <van-icon name="arrow-left" size="20" color="#333333" style="font-weight:600;" />
      </div>
      <div class="header-title">{{ orderInfo.ORDNO || orderInfo.ORDERNO }} - 报工明细</div>
      <div class="header-right"></div>
    </div>

    <!-- 订单信息卡片 -->
    <div class="info-section">
      <div class="info-card">
        <div class="card-title">订单信息</div>
        <div class="info-row-wrapper">
          <div class="info-row">
            <span class="info-label">订单号</span>
            <span class="info-value">{{ orderInfo.ORDNO || orderInfo.ORDERNO }}</span>
          </div>
           <div class="info-row">
            <span class="info-label">订单数量</span>
            <span class="info-value highlight">{{ orderInfo.QTY || orderInfo.ORDERQTY }}</span>
          </div>
        </div>
        <div class="info-row-wrapper">
          <div class="info-row">
            <span class="info-label">产品名称</span>
            <span class="info-value">{{ orderInfo.MATNM || orderInfo.PRODNAME }}</span>
          </div>
        </div>
        <div class="info-row-wrapper">
          <div class="info-row">
            <span class="info-label">交货日期</span>
            <span class="info-value">{{ orderInfo.SHPDTTM || orderInfo.DELIVERYDATE }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">状态</span>
            <span class="info-value" :class="getStatusClass(orderInfo.STFG || orderInfo.STATUS)">{{ orderInfo.STFGNM || orderInfo.STATUS_NAME }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 报工列表标题 -->
    <div class="section-title">
      <span>报工工序</span>
      <span class="title-count">共 {{ processList.length }} 道工序</span>
    </div>

    <!-- 报工工序列表 -->
    <div class="process-list">
      <div v-if="processList.length === 0" class="empty-state">
        <van-icon name="todo-list-o" size="48" color="#CCCCCC" />
        <p>暂无工序数据</p>
      </div>
      <div v-else class="list-content">
        <div 
          v-for="(item, index) in processList" 
          :key="index" 
          class="process-item-card"
          @click="openDtlWin(item)"
        >
          <div class="item-header">
            <span class="item-no">{{ index + 1 }}</span>
            <span class="item-title">{{ item.PROCNM || item.PROCNAME }}</span>
            <div class="item-status" :class="(item.REPORTQTY || 0) > 0 ? 'status-done' : 'status-pending'">
              {{ (item.REPORTQTY || 0) > 0 ? '已报工' : '待报工' }}
            </div>
          </div>
          <div class="item-content">
            <div class="item-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: item.progressPercent + '%' }"></div>
              </div>
              <span class="progress-text">{{ item.REPORTQTY || 0 }} / {{ item.PLANQTY }}</span>
            </div>
            <van-icon name="arrow" size="16" color="#999999" />
          </div>
        </div>
      </div>
    </div>

    <!-- 底部统计和操作栏 -->
    <div class="footer-bar">
      <div class="footer-stats">
        工序进度：<span class="stats-count">{{ finishedProcessCount }}</span> / {{ processList.length }}
        <span class="stats-percent">({{ processPercent }}%)</span>
      </div>
      <div class="footer-actions">
        <van-button 
          class="footer-btn footer-btn-complete" 
          :disabled="!canComplete"
          @click="handleComplete"
          type="primary"
        >
          完 成
        </van-button>
      </div>
    </div>

    <!-- 报工明细弹窗 -->
    <van-popup v-model="showDtlPopup" position="bottom" round :style="{ height: '75%' }">
      <div class="dtl-popup-content">
        <div class="popup-header">
          <div class="popup-title">{{ currentProcess.PROCNM || currentProcess.PROCNAME }} - 报工明细</div>
          <van-icon name="cross" @click="showDtlPopup = false" />
        </div>
        
        <!-- 报工记录列表 -->
        <div class="dtl-list">
          <div v-if="dtlData.length === 0" class="empty-state" style="padding: 40px 0;">
            <van-icon name="todo-list-o" size="48" color="#CCCCCC" />
            <p>暂无报工记录</p>
          </div>
          <div v-else class="dtl-list-content">
            <div v-for="(row, idx) in dtlData" :key="idx" class="dtl-item">
              <div class="dtl-item-header">
                <span class="dtl-date">{{ row.DTTM }}</span>
                <span class="dtl-qty">报工数量: <span class="highlight">{{ row.QTY }}</span></span>
              </div>
              <div class="dtl-item-content">
                <span class="dtl-remark">{{ row.RMK || '无备注' }}</span>
              </div>
              <div class="dtl-item-actions">
                <van-button size="mini" type="primary" @click="editDtlRec(row)">编辑</van-button>
                <van-button size="mini" type="danger" @click="delDtlRec(row, idx)">删除</van-button>
              </div>
            </div>
          </div>
        </div>

        <!-- 新增报工表单 -->
        <div class="dtl-form">
          <van-field
            v-model="reportForm.DTTM"
            is-link
            readonly
            clickable
            label="报工日期"
            placeholder="请选择日期"
            @click="showDatePicker = true"
          />
          <van-field
            v-model="reportForm.QTY"
            type="digit"
            label="报工数量"
            placeholder="请输入报工数量"
          />
          <van-field
            v-model="reportForm.RMK"
            type="textarea"
            label="备注"
            placeholder="请输入备注"
            rows="2"
          />
          <div class="dtl-form-actions">
            <van-button round block type="primary" @click="saveDtlRec">
              {{ reportForm.ITMSEQ ? '更新' : '新增报工' }}
            </van-button>
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 日期选择器 -->
    <van-popup v-model="showDatePicker" position="bottom" round>
      <van-datetime-picker
        v-model="currentDate"
        type="date"
        :min-date="minDate"
        :max-date="maxDate"
        @confirm="onDateConfirm"
        @cancel="showDatePicker = false"
      />
    </van-popup>

    <!-- 提示框 -->
    <van-dialog v-if="showPrompt" v-model="showPrompt" :title="'提示'" show-cancel-button @confirm="showPrompt = false" @cancel="showPrompt = false">
      <div style="padding: 20px; text-align: center;">{{ promptMsg }}</div>
    </van-dialog>

    <!-- 加载中 -->
    <van-loading v-if="loading" type="spinner" class="custom-loading" />

  </div>
</template>

<script>
import { getBaseData, getTabColOrData } from '@/api/user';
import { Button, Icon, Popup, Field, DatetimePicker, Dialog, Toast, Loading } from 'vant-green';

export default {
  name: "WorkshopReportDetail",

  components: {
    'van-button': Button,
    'van-icon': Icon,
    'van-popup': Popup,
    'van-field': Field,
    'van-datetime-picker': DatetimePicker,
    'van-dialog': Dialog,
    'van-toast': Toast,
    'van-loading': Loading
  },

  data() {
    return {
      loading: false,
      orderId: '',
      orderNo: '',
      orderInfo: {},
      processList: [],
      
      // 报工明细弹窗
      showDtlPopup: false,
      currentProcess: {},
      dtlData: [],
      dtlCol: [],
      
      // 日期选择
      showDatePicker: false,
      currentDate: new Date(),
      minDate: new Date(2020, 0, 1),
      maxDate: new Date(),
      
      // 提示框 - 确保默认不显示
      showPrompt: false,
      promptMsg: '',
      
      // 报工表单
      reportForm: {
        DTTM: '',
        QTY: '',
        RMK: '',
        ITMSEQ: '',
        IDSEQ: '',
        LSTSEQ: '',
        JOBNO: ''
      },
      
      v_username: this.$cookies.get('v_telno'),
      v_corpid: this.$cookies.get('v_corpid'),
      v_usernm: this.$cookies.get('v_usernm'),
      v_url: 'xuper_auth/getTableRecMany'
    };
  },

  computed: {
    // 已完成的工序数量
    finishedProcessCount() {
      return this.processList.filter(item => (item.REPORTQTY || 0) >= item.PLANQTY).length
    },
    // 工序完成百分比
    processPercent() {
      if (this.processList.length === 0) return 0
      return Math.round((this.finishedProcessCount / this.processList.length) * 100)
    },
    // 是否可以完成（所有工序都完成）
    canComplete() {
      return this.processList.length > 0 && this.finishedProcessCount === this.processList.length
    }
  },

  mounted() {
    // 确保所有弹窗状态都是关闭的，防止自动弹出
    this.showPrompt = false
    this.showDtlPopup = false
    this.showDatePicker = false
    
    if (!this.v_username) {
      this.$router.push({ path: '/login' })
      return
    }
    
    this.orderId = this.$route.query.id
    this.orderNo = this.$route.query.orderno
    
    // 初始化日期
    this.initDate()
    
    // 加载订单信息
    this.loadOrderInfo()
    // 加载工序列表
    this.loadProcessList()
    // 加载报工汇总数据
    this.loadJobQtyData()
  },

  methods: {
    // 初始化日期
    initDate() {
      const now = new Date()
      this.currentDate = now
      this.reportForm.DTTM = [
        String(now.getFullYear()),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
      ].join('-')
    },

    // 日期确认
    onDateConfirm({ selectedValues }) {
      this.reportForm.DTTM = selectedValues.join('-')
      this.showDatePicker = false
    },

    // 返回
    onBack() {
      this.$router.go(-1)
    },

    // 获取状态样式
    getStatusClass(status) {
      const statusMap = {
        'N': 'status-pending',
        'Y': 'status-done',
        '0': 'status-pending',
        '1': 'status-processing',
        '2': 'status-done'
      }
      return statusMap[status] || ''
    },

    // 加载订单信息
    loadOrderInfo() {
      const cond = " and idseq='" + this.orderId + "'"
      getTabColOrData(this.v_url, 'V_ORD_PROC', '', '', 'corpid', this.v_corpid, '', '', cond).then((res) => {
        if (res.code == '200' && res.result.length > 0) {
          this.orderInfo = res.result[0]
        }
      })
    },

    // 加载工序列表（从 V_ORD_PROC 获取）
    loadProcessList() {
      const cond = " and idseq='" + this.orderId + "'"
      getTabColOrData(this.v_url, 'V_ORD_PROC', '', '', 'corpid', this.v_corpid, '', '', cond).then((res) => {
        if (res.code == '200') {
          const procData = res.result[0] || {}
          // 解析工序进度 JOBPROC 字段，格式: 工序名#工序号,工序名#工序号,...
          const jobProc = procData.JOBPROC || ''
          const procArr = jobProc.split(',').filter(item => item)
          
          if (procArr.length > 0) {
            this.processList = procArr.map((item, index) => {
              const arr = item.split('#')
              const procName = arr[0] || ''
              const jobNo = arr[1] || ''
              
              // 从 qtyArr 中获取已报工数量
              let reportQty = 0
              let pct = 0
              
              return {
                PROCNM: procName,
                JOBNO: jobNo,
                PLANQTY: procData.QTY || 0,
                REPORTQTY: reportQty,
                progressPercent: pct,
                IDSEQ: procData.IDSEQ,
                LSTSEQ: procData.LSTSEQ
              }
            })
          } else {
            this.processList = []
          }
        }
      })
    },

    // 加载报工汇总数据 (V_ORD_JOBGP)
    loadJobQtyData() {
      getBaseData('xuper_auth/getBaseData', 'CORPID', this.v_corpid, '', '', 'V_ORD_JOBGP', '').then((res) => {
        if (res.code == '200') {
          const qtyArr = res.result || []
          
          // 更新每个工序的报工数量
          this.processList.forEach(proc => {
            const jobRec = qtyArr.find(item => 
              item.IDSEQ === proc.IDSEQ && 
              item.LSTSEQ === proc.LSTSEQ && 
              item.JOBNO === proc.JOBNO
            )
            if (jobRec) {
              proc.REPORTQTY = parseInt(jobRec.QTY || 0)
              proc.progressPercent = proc.PLANQTY > 0 ? Math.round((proc.REPORTQTY / proc.PLANQTY) * 100) : 0
            }
          })
        }
      })
    },

    // 打开报工明细弹窗
    openDtlWin(item) {
      this.currentProcess = item
      // 格式化当前日期为字符串
      const now = this.currentDate
      const dateStr = [
        String(now.getFullYear()),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
      ].join('-')
      this.reportForm = {
        DTTM: dateStr,
        QTY: '',
        RMK: '',
        ITMSEQ: '',
        IDSEQ: item.IDSEQ,
        LSTSEQ: item.LSTSEQ,
        JOBNO: item.JOBNO
      }
      this.showDtlPopup = true
      // 加载该工序的报工明细
      this.getDtlist(item.IDSEQ, item.LSTSEQ, item.JOBNO)
    },

    // 获取报工明细数据 (V_ORD_JOBITM)
    getDtlist(v_idseq, v_lstseq, v_jobno) {
      this.$axios({
        method: 'post',
        url: 'xuper_auth/getBaseData',
        data: { 
          p_key: 'idseq', 
          p_value: v_idseq,
          p_key2: 'jobno', 
          p_value2: v_jobno, 
          p_table: 'V_ORD_JOBITM',
          p_cond: ' and lstseq=' + v_lstseq + ' order by dttm desc'
        },
      }).then((res) => {
        if (res.code == '200') {
          this.dtlData = res.result || []
          // 更新工序的已报工数量
          this.updateProcessReportQty()
        } else {
          this.showPromptMsg(res.result || '获取明细失败')
        }
      })
    },

    // 更新工序的已报工数量
    updateProcessReportQty() {
      const totalQty = this.dtlData.reduce((sum, row) => sum + (parseInt(row.QTY) || 0), 0)
      this.currentProcess.REPORTQTY = totalQty
      this.currentProcess.progressPercent = this.currentProcess.PLANQTY > 0 
        ? Math.round((totalQty / this.currentProcess.PLANQTY) * 100) 
        : 0
      
      // 更新 processList 中的对应工序
      const idx = this.processList.findIndex(p => p.JOBNO === this.currentProcess.JOBNO)
      if (idx > -1) {
        this.processList[idx].REPORTQTY = totalQty
        this.processList[idx].progressPercent = this.currentProcess.progressPercent
      }
    },

    // 编辑报工记录
    editDtlRec(row) {
      this.reportForm = {
        DTTM: row.DTTM,
        QTY: String(row.QTY),
        RMK: row.RMK || '',
        ITMSEQ: row.ITMSEQ,
        IDSEQ: row.IDSEQ,
        LSTSEQ: row.LSTSEQ,
        JOBNO: row.JOBNO
      }
    },

    // 删除报工记录
    delDtlRec(row, index) {
      this.$dialog.confirm({
        title: '提示',
        message: '确定要删除此报工记录吗？'
      }).then(() => {
        this.$axios({
          method: 'post',
          url: 'xuper_auth/delTabRec',
          data: { 
            p_key: 'idseq', 
            p_value: row.IDSEQ, 
            p_key2: 'lstseq', 
            p_value2: row.LSTSEQ, 
            p_key3: 'itmseq', 
            p_value3: row.ITMSEQ, 
            p_table: 'ORD_JOBITM' 
          },
        }).then((res) => {
          if (res.code == '200') {
            this.dtlData.splice(index, 1)
            this.$toast.success('删除成功')
            // 更新工序报工数量
            this.updateProcessReportQty()
            // 刷新报工汇总数据
            this.loadJobQtyData()
          } else {
            this.showPromptMsg(res.result || '删除失败')
          }
        })
      }).catch(() => {})
    },

    // 保存报工记录
    saveDtlRec() {
      if (!this.reportForm.DTTM) {
        this.showPromptMsg('报工日期不能为空')
        return
      }
      if (!this.reportForm.QTY || parseInt(this.reportForm.QTY) <= 0) {
        this.showPromptMsg('报工数量不能为空')
        return
      }

      const remainQty = this.currentProcess.PLANQTY - this.currentProcess.REPORTQTY
      if (this.reportForm.ITMSEQ) {
        // 编辑模式，检查数量限制（排除自身）
        const selfRec = this.dtlData.find(r => r.ITMSEQ === this.reportForm.ITMSEQ)
        const selfQty = selfRec ? parseInt(selfRec.QTY || 0) : 0
        const maxQty = remainQty + selfQty
        if (parseInt(this.reportForm.QTY) > maxQty) {
          this.showPromptMsg('报工数量不能超过剩余数量(' + maxQty + ')')
          return
        }
      } else {
        // 新增模式
        if (parseInt(this.reportForm.QTY) > remainQty) {
          this.showPromptMsg('报工数量不能超过剩余数量(' + remainQty + ')')
          return
        }
      }

      let v_obj = {}
      if (this.reportForm.ITMSEQ) {
        // 编辑
        v_obj = {
          IDSEQ: this.reportForm.IDSEQ,
          LSTSEQ: this.reportForm.LSTSEQ,
          JOBNO: this.reportForm.JOBNO,
          JOBNM: this.currentProcess.PROCNM,
          DTTM: this.reportForm.DTTM,
          QTY: this.reportForm.QTY,
          RMK: this.reportForm.RMK,
          ITMSEQ: this.reportForm.ITMSEQ
        }
      } else {
        // 新增
        v_obj = {
          IDSEQ: this.reportForm.IDSEQ,
          LSTSEQ: this.reportForm.LSTSEQ,
          ITMSEQ: '',
          ORDNO: this.orderInfo.ORDNO,
          JOBNO: this.reportForm.JOBNO,
          JOBNM: this.currentProcess.PROCNM,
          DTTM: this.reportForm.DTTM,
          QTY: this.reportForm.QTY,
          RMK: this.reportForm.RMK
        }
      }

      this.$axios({
        method: 'post',
        url: 'xuper_auth/tableBodySave2',
        data: { p_json: JSON.stringify(v_obj), p_table: 'ORD_JOBITM' },
      }).then((res) => {
        if (res.code == '200') {
          this.$toast.success(this.reportForm.ITMSEQ ? '更新成功' : '报工成功')
          // 刷新明细数据
          this.getDtlist(this.currentProcess.IDSEQ, this.currentProcess.LSTSEQ, this.currentProcess.JOBNO)
          // 刷新报工汇总数据
          this.loadJobQtyData()
          // 重置表单
          const now = this.currentDate
          const dateStr = [
            String(now.getFullYear()),
            String(now.getMonth() + 1).padStart(2, '0'),
            String(now.getDate()).padStart(2, '0')
          ].join('-')
          this.reportForm = {
            DTTM: dateStr,
            QTY: '',
            RMK: '',
            ITMSEQ: '',
            IDSEQ: this.currentProcess.IDSEQ,
            LSTSEQ: this.currentProcess.LSTSEQ,
            JOBNO: this.currentProcess.JOBNO
          }
        } else {
          if (res.result && res.result.indexOf('ORA-00001') > -1) {
            this.showPromptMsg('已存在同日期下的记录，请核查')
          } else {
            this.showPromptMsg(res.result || '操作失败')
          }
        }
      })
    },

    // 显示提示信息
    showPromptMsg(msg) {
      this.promptMsg = msg
      this.showPrompt = true
    },

    // 完成订单
    handleComplete() {
      if (!this.canComplete) {
        this.$toast('请先完成所有工序')
        return
      }
      this.$dialog.confirm({
        title: '提示',
        message: '确定要完成订单吗？'
      }).then(() => {
        this.$toast.success('订单已完成')
        this.$router.go(-1)
      }).catch(() => {})
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #F7F8FA;
  overflow: hidden;
  position: relative;
}

// 自定义加载
.custom-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
}

// 顶部导航栏
.header-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: #F7F8FA;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 999;
  flex-shrink: 0;

  .header-left {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .header-title {
    flex: 1;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    line-height: 28px;
    color: #333333;
  }

  .header-right {
    width: 40px;
  }
}

// 信息卡片
.info-section {
  margin-top: 56px;
  padding: 12px;
  flex-shrink: 0;

  .info-card {
    background: #FFFFFF;
    border-radius: 8px;
    padding: 12px 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .card-title {
      font-size: 15px;
      font-weight: 600;
      color: #333333;
      margin-bottom: 12px;
      padding-left: 10px;
      border-left: 3px solid #235EFF;
    }

    .info-row-wrapper {
      display: flex;
      gap: 12px;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .info-row {
      display: flex;
      flex: 1;
      font-size: 13px;
      line-height: 22px;

      .info-label {
        color: #666666;
        min-width: 70px;
        flex-shrink: 0;
      }

      .info-value {
        color: #333333;
        flex: 1;

        &.highlight {
          color: #235EFF;
          font-weight: 500;
        }

        &.status-processing {
          color: #235EFF;
        }

        &.status-pending {
          color: #FDC71C;
        }

        &.status-done {
          color: #1ECD84;
        }
      }
    }
  }
}

// 标题栏
.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #333333;
  background: #F7F8FA;
  flex-shrink: 0;

  .title-count {
    font-size: 13px;
    font-weight: 400;
    color: #999999;
  }
}

// 工序列表
.process-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px;
  padding-bottom: 120px;

  .empty-state {
    padding: 60px 0;
    text-align: center;

    p {
      margin-top: 12px;
      color: #999999;
      font-size: 14px;
    }
  }

  .list-content {
    .process-item-card {
      background: #FFFFFF;
      border-radius: 8px;
      padding: 14px;
      margin-bottom: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
      cursor: pointer;
      transition: all 0.2s;

      &:active {
        transform: scale(0.98);
        background: #F7F8FA;
      }

      .item-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;

        .item-no {
          width: 24px;
          height: 24px;
          background: #235EFF;
          color: #FFFFFF;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          flex-shrink: 0;
        }

        .item-title {
          flex: 1;
          font-size: 15px;
          font-weight: 600;
          color: #333333;
        }

        .item-status {
          font-size: 12px;
          padding: 2px 10px;
          border-radius: 10px;
          font-weight: 500;

          &.status-pending {
            background: rgba(253, 199, 28, 0.1);
            color: #FDC71C;
          }

          &.status-done {
            background: rgba(30, 205, 132, 0.1);
            color: #1ECD84;
          }
        }
      }

      .item-content {
        display: flex;
        align-items: center;
        gap: 12px;

        .item-progress {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 12px;

          .progress-bar {
            flex: 1;
            height: 8px;
            background: #F0F0F0;
            border-radius: 4px;
            overflow: hidden;

            .progress-fill {
              height: 100%;
              background: linear-gradient(90deg, #235EFF, #1ECD84);
              border-radius: 4px;
              transition: width 0.3s ease;
            }
          }

          .progress-text {
            font-size: 13px;
            color: #666666;
            min-width: 70px;
            text-align: right;
          }
        }
      }
    }
  }
}

// 底部操作栏
.footer-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #FFFFFF;
  border-top: 1px solid #EBEDF0;
  padding: 12px 16px;
  z-index: 100;

  .footer-stats {
    font-size: 14px;
    line-height: 22px;
    color: #666666;
    margin-bottom: 12px;
    
    .stats-count {
      color: #235EFF;
      font-weight: 600;
      font-size: 16px;
    }
    
    .stats-percent {
      color: #999999;
      margin-left: 4px;
    }
  }

  .footer-actions {
    .footer-btn {
      width: 100%;
      height: 44px;
      border: none;
      border-radius: 22px;
      font-size: 16px;
      font-weight: 500;

      &.footer-btn-complete {
        background: #1ECD84 !important;
        color: #FFFFFF !important;

        &:active {
          background: #12A66A !important;
        }
        
        &.van-button--disabled {
          background: #CCCCCC !important;
          color: #FFFFFF !important;
        }
      }
    }
  }
}

// 报工明细弹窗样式
.dtl-popup-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;

  .popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-shrink: 0;

    .popup-title {
      font-size: 16px;
      font-weight: 600;
      color: #333333;
    }
  }

  .dtl-list {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 16px;
    border-bottom: 1px solid #EBEDF0;

    .dtl-list-content {
      .dtl-item {
        background: #F7F8FA;
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 10px;

        .dtl-item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;

          .dtl-date {
            font-size: 14px;
            color: #333333;
            font-weight: 500;
          }

          .dtl-qty {
            font-size: 13px;
            color: #666666;

            .highlight {
              color: #235EFF;
              font-weight: 600;
            }
          }
        }

        .dtl-item-content {
          margin-bottom: 10px;

          .dtl-remark {
            font-size: 13px;
            color: #999999;
          }
        }

        .dtl-item-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
      }
    }
  }

  .dtl-form {
    flex-shrink: 0;

    .dtl-form-actions {
      margin-top: 16px;
    }
  }
}

// 覆盖 van-field 样式
::v-deep .van-field {
  padding: 12px 16px;
  background: #F7F8FA;
  border-radius: 8px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  .van-field__label {
    color: #333333;
    font-size: 14px;
    font-weight: 500;
  }

  .van-field__body {
    .van-field__control {
      color: #333333;
      font-size: 14px;
    }
  }
}

// 强制覆盖 van-button 默认样式
::v-deep .footer-btn.van-button {
  border: none !important;
  background: #1ECD84 !important;
  
  .van-button__text {
    color: #FFFFFF !important;
  }
}
</style>
